import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { ClipboardPaste, FileText, Landmark, Upload, X } from "lucide-react";
import { geographyText, useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Input, NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, cn, notifySaved, toast } from "@ncp/ui";
import { useSectors } from "../databank/api";
import { useAdvocacyFramework, useCreateReview } from "./api";

const ACCEPT = { "application/pdf": [".pdf"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "text/plain": [".txt"], "text/markdown": [".md"], "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] };
const MAX_BYTES = 25 * 1024 * 1024;
const STAGES = ["Draft", "InForce", "Proposed", "Consultation"] as const;
/** Federal instruments only, until the Department sets the section's scope (local orders are outside it meanwhile). */
const JURISDICTIONS = ["Federal"];

const empty = { title: "", titleAr: "", issuingAuthority: "", jurisdiction: "Federal", instrumentType: "Policy", stage: "Draft", reference: "", sectorId: "", notes: "", text: "" };

/** Records a law, regulation or policy – a file or its text – with its metadata; while the section's method awaits the Department's approval nothing is screened and no agent runs. */
export function NewReviewDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const sectors = useSectors();
  const framework = useAdvocacyFramework();
  const create = useCreateReview();
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<"upload" | "paste">("upload");
  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onDrop = useCallback((accepted: File[], rejected: FileRejection[]) => {
    if (rejected.length > 0) { toast.error(t("ai.chat.uploadFailed", { name: rejected[0]?.file.name ?? "" }), { description: rejected[0]?.errors[0]?.message }); return; }
    const f = accepted[0];
    if (f) { setFile(f); if (!form.title) setForm((x) => ({ ...x, title: f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ") })); }
  }, [form.title, t]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ACCEPT, maxFiles: 1, maxSize: MAX_BYTES, multiple: false, disabled: create.isPending });

  const canSubmit = form.title.trim().length > 0 && form.issuingAuthority.trim().length > 0 && (mode === "upload" ? !!file : form.text.trim().length >= 40);
  const submit = () => {
    if (!canSubmit) { toast.error(t("backoffice:advocacy.dialog.needText")); return; }
    const fd = new FormData();
    for (const [k, v] of Object.entries(form)) { if (k === "text") continue; if (v) fd.append(k, v); }
    if (mode === "upload" && file) fd.append("file", file); else fd.append("text", form.text);
    create.mutate(fd, {
      onSuccess: (r) => {
        notifySaved(r, r.queued ? t("backoffice:advocacy.dialog.created", { provisions: r.provisionCount, flagged: r.flaggedProvisions, index: r.restrictivenessIndex }) : t("backoffice:r3Library.advocacy.recorded"));
        setForm(empty); setFile(null); onOpenChange(false); onCreated(r.id);
      },
    });
  };

  const types = framework.data?.instrumentTypes ?? ["Law", "DecreeLaw", "CabinetResolution", "MinisterialDecision", "Circular", "Policy", "Guideline", "DraftBill", "Other"];
  const jurisdictions = framework.data?.jurisdictions ?? JURISDICTIONS;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!create.isPending) onOpenChange(o); }}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="size-5 text-aegold-700" /> {t("backoffice:advocacy.dialog.title")}</DialogTitle>
          <DialogDescription>{t("backoffice:advocacy.dialog.hint")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-5">
          <div className="space-y-3 md:col-span-3">
            <Tabs value={mode} onValueChange={(v) => setMode(v as "upload" | "paste")}>
              <TabsList className="mb-3">
                <TabsTrigger value="upload"><Upload /> {t("backoffice:advocacy.dialog.upload")}</TabsTrigger>
                <TabsTrigger value="paste"><ClipboardPaste /> {t("backoffice:advocacy.dialog.paste")}</TabsTrigger>
              </TabsList>
              <TabsContent value="upload">
                <div {...getRootProps()} className={cn("flex min-h-44 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-sand-50/60 p-6 text-center transition-colors", isDragActive ? "border-aegold-500 bg-aegold-50" : "border-sand-300 hover:border-aegold-300")}>
                  <input {...getInputProps()} />
                  {file ? (
                    <>
                      <FileText className="size-8 text-aegold-700" />
                      <p className="text-sm font-medium text-aeblack-950" dir="auto">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
                      <Button type="button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setFile(null); }}><X /> {t("actions.delete")}</Button>
                    </>
                  ) : (
                    <>
                      <Upload className="size-8 text-sand-500" />
                      <p className="text-sm font-medium text-aeblack-950">{t("backoffice:advocacy.dialog.dropzone")}</p>
                      <p className="text-xs text-muted-foreground">{t("backoffice:advocacy.dialog.dropzoneHint")}</p>
                    </>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="paste">
                <Textarea value={form.text} onChange={set("text")} draft="advocacy:new:text" rows={9} className="min-h-44 font-mono text-xs" dir="auto" placeholder={t("backoffice:advocacy.dialog.textPlaceholder")} />
              </TabsContent>
            </Tabs>
            <Field label={t("backoffice:advocacy.dialog.notes")}>
              {(id) => <Textarea id={id} value={form.notes} onChange={set("notes")} draft="advocacy:new:notes" rows={3} dir="auto" placeholder={t("backoffice:advocacy.dialog.notesPlaceholder")} />}
            </Field>
          </div>
          <div className="space-y-3 md:col-span-2">
            <Field label={t("backoffice:advocacy.dialog.titleLabel")} required>{(id) => <Input id={id} value={form.title} onChange={set("title")} dir="auto" placeholder={t("backoffice:advocacy.dialog.titlePlaceholder")} />}</Field>
            <Field label={t("backoffice:advocacy.dialog.titleArLabel")}>{(id) => <Input id={id} value={form.titleAr} onChange={set("titleAr")} dir="rtl" />}</Field>
            <Field label={t("backoffice:advocacy.dialog.authority")} required>{(id) => <Input id={id} value={form.issuingAuthority} onChange={set("issuingAuthority")} dir="auto" placeholder={t("backoffice:advocacy.dialog.authorityPlaceholder")} />}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("backoffice:advocacy.dialog.instrumentType")}>
                {(id) => <NativeSelect id={id} value={form.instrumentType} onChange={set("instrumentType")}>{types.map((x) => <option key={x} value={x}>{t(`backoffice:advocacy.instrumentTypes.${x}`, { defaultValue: x })}</option>)}</NativeSelect>}
              </Field>
              <Field label={t("backoffice:advocacy.dialog.stage")}>
                {(id) => <NativeSelect id={id} value={form.stage} onChange={set("stage")}>{STAGES.map((x) => <option key={x} value={x}>{t(`backoffice:advocacy.stage.${x}`)}</option>)}</NativeSelect>}
              </Field>
              <Field label={t("backoffice:advocacy.dialog.jurisdiction")}>
                {(id) => <NativeSelect id={id} value={form.jurisdiction} onChange={set("jurisdiction")}>{jurisdictions.map((x) => <option key={x} value={x}>{geographyText(x, locale)}</option>)}</NativeSelect>}
              </Field>
              <Field label={t("backoffice:advocacy.dialog.reference")}>{(id) => <Input id={id} value={form.reference} onChange={set("reference")} dir="auto" placeholder={t("backoffice:advocacy.dialog.referencePlaceholder")} />}</Field>
            </div>
            <Field label={t("backoffice:advocacy.dialog.sector")}>
              {(id) => (
                <NativeSelect id={id} value={form.sectorId} onChange={set("sectorId")}>
                  <option value="">{t("backoffice:advocacy.dialog.noSector")}</option>
                  {(sectors.data ?? []).map((s) => <option key={s.id} value={s.id}>{locale === "ar" ? s.nameAr : s.nameEn}</option>)}
                </NativeSelect>
              )}
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>{t("actions.cancel")}</Button>
          <Button variant="gold" onClick={submit} loading={create.isPending} disabled={!canSubmit}>{create.isPending ? t("backoffice:advocacy.dialog.submitting") : t("backoffice:advocacy.dialog.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
